import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { trackPaymentCompleted } from '../services/analytics';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get parameters from payment redirect
  const gateway = searchParams.get('gateway');
  const transaction = searchParams.get('transaction') || searchParams.get('off') || searchParams.get('OrderTrackingId');
  const merchantRef = searchParams.get('OrderMerchantReference');
  const email = searchParams.get('email') || user?.email;
  const subscriptionId = searchParams.get('subscription_id');
  const planFromUrl = searchParams.get('plan');

  // Extract plan type from merchant reference (e.g., SQ-STUDENT-xxx -> student)
  const getPlanFromMerchantRef = (ref) => {
    if (!ref) return null;
    if (ref.includes('STUDENT')) return 'student';
    if (ref.includes('TEACHER')) return 'teacher';
    if (ref.includes('GROUP')) return 'group';
    return null;
  };

  const planType = planFromUrl || getPlanFromMerchantRef(merchantRef);

  useEffect(() => {
    // Check subscription status if we have transaction info
    const checkSubscription = async () => {
      // PayPal verification
      if (gateway === 'paypal' && subscriptionId) {
        try {
          // Verify and activate the subscription
          const response = await axios.post(`${API_URL}/api/paypal/verify-subscription/${subscriptionId}`);
          setSubscriptionStatus(response.data);
          console.log('PayPal subscription verified:', response.data);
          
          // Track subscription completed
          const price = planType === 'student' ? 4 : (planType === 'teacher' ? 6 : 20);
          trackPaymentCompleted(planType, 'paypal', price);
        } catch (error) {
          console.log('Could not verify PayPal subscription:', error);
          // Try to get status even if verification fails
          try {
            const statusResponse = await axios.get(`${API_URL}/api/paypal/subscription-status/${subscriptionId}`);
            setSubscriptionStatus(statusResponse.data);
          } catch (e) {
            console.log('Could not get subscription status');
          }
        }
      }
      // Pesapal verification
      else if (transaction && gateway === 'pesapal') {
        try {
          // Query transaction status
          const response = await axios.get(`${API_URL}/api/pesapal/transaction-status/${transaction}`);
          setSubscriptionStatus(response.data);
          
          // Track subscription completed if payment was successful
          if (response.data?.status === 'COMPLETED' || response.data?.payment_status_description === 'Completed') {
            const price = planType === 'student' ? 10000 : (planType === 'teacher' ? 15000 : 50000);
            trackPaymentCompleted(planType, 'pesapal', price);
          }
        } catch (error) {
          console.log('Could not check transaction status');
        }
      }
      setLoading(false);
    };

    checkSubscription();
  }, [transaction, gateway, subscriptionId, planType]);

  useEffect(() => {
    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert('To install: Click the menu button (⋮) in your browser and select "Install App" or "Add to Home Screen"');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstalled(true);
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  const planNames = {
    student: 'Student Plan',
    teacher: 'Teacher Pro Plan',
    group: 'Group Plan'
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-green-50 to-emerald-100'} flex items-center justify-center p-4`}>
      <div className={`max-w-lg w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl p-8 text-center`}>
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Success Message */}
        <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Payment Successful! 🎉
        </h1>
        <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Thank you for your purchase!
        </p>
        
        {/* Plan Info */}
        {planType && (
          <div className={`inline-flex items-center px-4 py-2 rounded-full mb-6 ${
            planType === 'student' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' :
            planType === 'teacher' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
            'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
          }`}>
            <span className="mr-2">
              {planType === 'student' ? '🎓' : planType === 'teacher' ? '👩‍🏫' : '🏫'}
            </span>
            {planNames[planType]} Activated!
          </div>
        )}

        {/* Transaction Info */}
        {transaction && (
          <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Transaction ID: <span className="font-mono text-xs">{transaction}</span>
            </p>
            {email && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Email: {email}
              </p>
            )}
          </div>
        )}

        {/* Install App Section */}
        <div className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-gradient-to-r from-purple-900 to-indigo-900' : 'bg-gradient-to-r from-purple-600 to-indigo-600'} text-white`}>
          <div className="text-4xl mb-3">📱</div>
          <h2 className="text-xl font-bold mb-2">Install Study Quiz App</h2>
          <p className="text-sm opacity-90 mb-4">
            Add to your home screen for quick access - works offline too!
          </p>

          {installed ? (
            <div className="flex items-center justify-center text-green-300">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              App Installed!
            </div>
          ) : (
            <button
              onClick={handleInstallClick}
              className="w-full py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-gray-100 transition-all flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Install App
            </button>
          )}

          {/* Manual Install Instructions */}
          <div className="mt-4 text-xs opacity-75">
            <p><strong>Chrome/Edge:</strong> Menu (⋮) → Install App</p>
            <p><strong>Safari (iOS):</strong> Share → Add to Home Screen</p>
          </div>
        </div>

        {/* Next Steps */}
        <div className={`text-left p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
          <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            ✨ What's Next?
          </h3>
          <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <li>✓ Your subscription is already active</li>
            <li>✓ Generate unlimited quizzes</li>
            <li>✓ Access all premium features</li>
            <li>✓ Install the app for offline access</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {user ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Go to Dashboard →
            </button>
          ) : (
            <>
              {/* For Pesapal payments - guide to login since subscription is linked to email */}
              {gateway === 'pesapal' ? (
                <>
                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Your {planType ? planNames[planType] : 'subscription'} is linked to your email. Log in or create an account with the same email to access your premium features.
                  </p>
                  <button
                    data-testid="pesapal-login-btn"
                    onClick={() => navigate('/login')}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:from-indigo-700 hover:to-purple-700 transition-all"
                  >
                    Log In to Your Account →
                  </button>
                  <button
                    data-testid="pesapal-create-account-btn"
                    onClick={() => navigate(`/register?plan=${planType || 'student'}&paid=true`)}
                    className={`w-full py-3 border-2 rounded-lg font-medium transition-all ${
                      isDarkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    New here? Create Account
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:from-indigo-700 hover:to-purple-700 transition-all"
                  >
                    Create Your Account →
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className={`w-full py-3 border-2 rounded-lg font-medium transition-all ${
                      isDarkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Already have an account? Log In
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Help Link */}
        <p className={`mt-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Need help? <a href="mailto:support@aiquiz.com" className="text-indigo-600 hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
